import {
  FormLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { commonErrorMessage } from 'utils/error-message';

interface NumberFieldProps {
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error?: any;
  defaultValue?: number;
  value?: number;
  range?: { min?: number; max?: number };
  isDecima?: boolean;
  onChange?: (value?: number) => void;
  onChangeSize?: React.ChangeEventHandler<HTMLInputElement>;
}

const NumberField: FC<NumberFieldProps> = ({
  id,
  label,
  required,
  placeholder,
  value: initValue,
  range,
  isDecima = false,
  onChange,
  onChangeSize,
  error,
  disabled,
}) => {
  const theme = useTheme();
  const [value, setValue] = useState<string | undefined>();
  const [isBlur, setIsBlur] = useState<boolean>(false);

  const preventCharacters = useMemo(() => {
    return isDecima ? ['e', 'E', '-', ','] : ['e', 'E', '-', '.', ','];
  }, [isDecima]);

  useEffect(() => {
    setValue(initValue?.toString());
  }, [initValue]);

  useEffect(() => {
    if (!isBlur) return;
    const fixedValue = Number(Number(value).toFixed(2));
    if (fixedValue || fixedValue === 0) {
      setValue(isDecima ? fixedValue.toString() : value);
      onChange?.(Number(fixedValue));
    } else {
      setValue(undefined);
      onChange?.(undefined);
    }
    setIsBlur(false);
  }, [isBlur]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setValue(value ? value : undefined);
    if (onChangeSize) onChangeSize?.(event);
  };

  const handleBlur = () => {
    if (range?.min && Number(value ?? 0) < range.min) {
      setValue(range.min.toString());
    } else if (range?.max && Number(value ?? 0) > range.max) {
      setValue(range.max.toString());
    }
    setIsBlur(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (preventCharacters.includes(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <Grid container direction='column' sx={{ gap: { xs: '4px', sm: '10px' } }}>
      {label && (
        <Grid
          item
          xs={4}
          alignItems='center'
          justifyContent='flex-start'
          sx={{ color: '#000000de' }}
        >
          <FormLabel
            htmlFor={id}
            required={required}
            sx={{ color: '#000000de' }}
          >
            {label}
          </FormLabel>
        </Grid>
      )}
      <Grid item>
        <TextField
          id={id}
          name={id}
          type='number'
          fullWidth
          value={value != '0' && value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          inputProps={{
            sx: {
              padding: '10.63px',
            },
            step: isDecima ? 'any' : 1,
            ...range,
          }}
          sx={{
            '& input[type=number]': {
              '-webkit-appearance': 'textfield',
              '-moz-appearance': 'textfield',
              '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                display: 'none',
              },
            },
          }}
          error={!!error}
        />
        {error && (
          <Typography
            sx={{
              color: theme.palette.error.main,
              fontSize: theme.typography.fontSize,
              my: '5px',
            }}
          >
            {error.message || commonErrorMessage.MSE00003(label ?? '')}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

export default NumberField;
